import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { Send, Mail, MapPin, ClipboardCheck } from "lucide-react";

const EMAILJS_SERVICE = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const CONTACT_EMAIL =
  process.env.REACT_APP_CONTACT_EMAIL || "support@bluelinecontrolsystems.com";

const EMAILJS_READY = Boolean(
  EMAILJS_SERVICE && EMAILJS_TEMPLATE && EMAILJS_PUBLIC_KEY
);

const PROJECT_TYPES = [
  "Project Scheduling",
  "Critical Path Analysis",
  "Schedule Development",
  "Progress Tracking",
  "Schedule Recovery",
  "Delay / Controls Support",
  "Other",
];

const initialForm = {
  name: "",
  email: "",
  company: "",
  phone: "",
  project_type: "",
  message: "",
};

const buildMailto = (f) => {
  const subject = `[BlueLine Inquiry] ${f.project_type || "New project"} — ${f.name}`;
  const body = [
    `Name: ${f.name}`,
    `Email: ${f.email}`,
    `Company: ${f.company || "-"}`,
    `Phone: ${f.phone || "-"}`,
    `Project Type: ${f.project_type || "-"}`,
    "",
    "Message:",
    f.message,
  ].join("\n");
  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
};

const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !isEmail(form.email) || form.message.trim().length < 5) {
      toast.error(
        "Please enter your name, a valid email, and a brief message (5+ chars)."
      );
      return;
    }

    setSubmitting(true);
    try {
      if (EMAILJS_READY) {
        await emailjs.send(
          EMAILJS_SERVICE,
          EMAILJS_TEMPLATE,
          {
            from_name: form.name,
            reply_to: form.email,
            company: form.company || "—",
            phone: form.phone || "—",
            project_type: form.project_type || "—",
            message: form.message,
            to_email: CONTACT_EMAIL,
          },
          { publicKey: EMAILJS_PUBLIC_KEY }
        );
        setSubmitted(true);
        setForm(initialForm);
        toast.success(
          "Inquiry sent. We'll be in touch within one business day."
        );
      } else {
        // Fallback: open the user's mail client with a prefilled message
        window.location.href = buildMailto(form);
        setSubmitted(true);
        setForm(initialForm);
        toast.success(
          "Opening your email app — your draft is ready to send."
        );
      }
    } catch (err) {
      const detail =
        err?.text || err?.message || "Could not send. Please try again.";
      toast.error(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="contact-section"
      id="contact"
      className="relative py-24 md:py-32 bg-[#04070D] overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left intro */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#00E5FF]" />
              <span className="font-mono-accent text-[11px] text-[#00E5FF]">
                / Start the conversation
              </span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#F1F5F9] leading-[1.05] mb-8">
              Request a <span className="text-[#FFB300]">proposal</span>.
            </h2>
            <p className="text-[#94A3B8] text-base lg:text-lg leading-relaxed mb-12 max-w-md">
              Tell us about your project — baseline build, schedule audit,
              recovery plan, or full controls support. We respond within one
              business day.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[#1E2A3B] flex items-center justify-center flex-shrink-0">
                  <Mail size={16} className="text-[#00E5FF]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-mono-accent text-[10px] text-[#4A6E8C] mb-1">
                    / Inquiries
                  </div>
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="text-[#F1F5F9] text-sm hover:text-[#00E5FF] transition-colors"
                  >
                    {CONTACT_EMAIL}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[#1E2A3B] flex items-center justify-center flex-shrink-0">
                  <MapPin
                    size={16}
                    className="text-[#00E5FF]"
                    strokeWidth={1.5}
                  />
                </div>
                <div>
                  <div className="font-mono-accent text-[10px] text-[#4A6E8C] mb-1">
                    / Coverage
                  </div>
                  <div className="text-[#F1F5F9] text-sm">
                    Nationwide / Remote-first
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[#1E2A3B] flex items-center justify-center flex-shrink-0">
                  <ClipboardCheck
                    size={16}
                    className="text-[#00E5FF]"
                    strokeWidth={1.5}
                  />
                </div>
                <div>
                  <div className="font-mono-accent text-[10px] text-[#4A6E8C] mb-1">
                    / Response Time
                  </div>
                  <div className="text-[#F1F5F9] text-sm">
                    Within 1 business day
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <div className="border border-[#1E2A3B] glass-panel p-8 lg:p-10 relative">
              <div className="absolute top-0 left-0 w-2 h-2 bg-[#FFB300]" />
              <div className="absolute top-0 right-0 w-2 h-2 bg-[#00E5FF]" />

              {submitted ? (
                <div
                  data-testid="contact-success"
                  className="py-20 text-center animate-float-up"
                >
                  <div className="w-14 h-14 mx-auto mb-6 border border-[#FFB300] flex items-center justify-center">
                    <ClipboardCheck
                      size={22}
                      className="text-[#FFB300]"
                      strokeWidth={1.5}
                    />
                  </div>
                  <div className="font-mono-accent text-[11px] text-[#FFB300] mb-3">
                    / Inquiry sent
                  </div>
                  <h3 className="font-heading text-3xl text-[#F1F5F9] mb-4">
                    {EMAILJS_READY
                      ? "Received. Thank you."
                      : "Email draft prepared."}
                  </h3>
                  <p className="text-[#94A3B8] max-w-md mx-auto text-sm leading-relaxed mb-8">
                    {EMAILJS_READY
                      ? "A BlueLine team member will reach out within one business day with next steps."
                      : "We opened your email app with a prefilled message. Hit send and we'll respond within one business day."}
                  </p>
                  <button
                    data-testid="contact-submit-another"
                    onClick={() => setSubmitted(false)}
                    className="btn-ghost"
                  >
                    Submit Another
                  </button>
                </div>
              ) : (
                <form
                  data-testid="contact-form"
                  onSubmit={submit}
                  className="space-y-5"
                >
                  <div className="font-mono-accent text-[10px] text-[#4A6E8C] mb-2">
                    / Project Inquiry — Form 01
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block font-mono-accent text-[10px] text-[#94A3B8] mb-2">
                        Name *
                      </label>
                      <input
                        data-testid="contact-input-name"
                        className="input-blueline"
                        type="text"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block font-mono-accent text-[10px] text-[#94A3B8] mb-2">
                        Email *
                      </label>
                      <input
                        data-testid="contact-input-email"
                        className="input-blueline"
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="you@firm.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block font-mono-accent text-[10px] text-[#94A3B8] mb-2">
                        Company
                      </label>
                      <input
                        data-testid="contact-input-company"
                        className="input-blueline"
                        type="text"
                        value={form.company}
                        onChange={update("company")}
                        placeholder="Company / firm"
                      />
                    </div>
                    <div>
                      <label className="block font-mono-accent text-[10px] text-[#94A3B8] mb-2">
                        Phone
                      </label>
                      <input
                        data-testid="contact-input-phone"
                        className="input-blueline"
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="(optional)"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-mono-accent text-[10px] text-[#94A3B8] mb-2">
                      Project Type
                    </label>
                    <select
                      data-testid="contact-input-type"
                      className="input-blueline appearance-none cursor-pointer"
                      value={form.project_type}
                      onChange={update("project_type")}
                    >
                      <option value="">Select a service area…</option>
                      {PROJECT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block font-mono-accent text-[10px] text-[#94A3B8] mb-2">
                      Project Brief *
                    </label>
                    <textarea
                      data-testid="contact-input-message"
                      className="input-blueline min-h-[140px] resize-y"
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Scope, timeline, current pain points…"
                      required
                    />
                  </div>

                  <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <span className="font-mono-accent text-[10px] text-[#4A6E8C]">
                      {EMAILJS_READY
                        ? "Sent directly · response within 1 business day"
                        : "Opens your email app · no backend required"}
                    </span>
                    <button
                      data-testid="contact-submit-button"
                      type="submit"
                      disabled={submitting}
                      className="btn-amber disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? "Sending…" : "Send Inquiry"}
                      {!submitting && <Send size={14} />}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
